import styled, { css } from 'styled-components';

// Font styles
const crFont9 = css`
  font-family: 'Open Sans';
  font-size: 9px;
  font-style: normal;
  font-weight: 700;
  line-height: 11px;
`;

const crFont11 = css`
  font-family: 'Open Sans';
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 13px;
`;

// Custom scroll styles
const customSlimScroll = css`
  &::-webkit-scrollbar {
    width: 4px; /* Mostly for vertical scrollbars */
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.dataTable.scrollbarThumbColor};
    border-radius: 1px;
    &:hover {
      background: ${({ theme }) => theme.dataTable.scrollbarThumbHoverColor};
    }
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.dataTable.scrollbarTrackColor};
  }
`;

export const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.dataTable.backgroundColor};
  border: 1px solid ${({ theme }) => theme.dataTable.borderColor};
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
`;

export const DataTableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.dataTable.headerBackgroundColor};
  border-bottom: 1px solid ${({ theme }) => theme.dataTable.borderColor};
`;

export const TableWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  ${customSlimScroll}

  /* Row styling for data table */
  .data-table-row.even {
    background-color: ${({ theme }) => theme.dataTable.rowBackgroundColorEven};
  }

  .data-table-row.odd {
    background-color: ${({ theme }) => theme.dataTable.rowBackgroundColor};
  }

  .data-table-row:hover {
    background-color: ${({ theme }) => theme.dataTable.rowHoverBackgroundColor};
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  th,
  td {
    box-sizing: border-box;
  }
`;

export const TableHeader = styled.thead`
  position: sticky;
  top: 0;
  z-index: 10;
`;

export const TableBody = styled.tbody`
  tr:nth-child(even) {
    background-color: ${({ theme }) => theme.dataTable.rowBackgroundColorEven};
  }
  tr:nth-child(odd) {
    background-color: ${({ theme }) => theme.dataTable.rowBackgroundColor};
  }
  tr:hover {
    background-color: ${({ theme }) => theme.dataTable.rowHoverBackgroundColor};
  }
`;

export const HeaderRow = styled.tr`
  background-color: ${({ theme }) => theme.dataTable.headerBackgroundColor};
`;

export const HeaderCell = styled.th<{ width: number; isResizing?: boolean }>`
  position: relative;
  width: ${({ width }) => width}px;
  min-width: ${({ width }) => width}px;
  max-width: ${({ width }) => width}px;
  height: 30px;
  padding: 0 12px;
  background-color: ${({ theme }) => theme.dataTable.headerBackgroundColor};
  color: ${({ theme }) => theme.dataTable.headerTextColor};
  vertical-align: middle;
  border-right: 1px solid ${({ theme }) => theme.dataTable.headerBorderColor};
  border-bottom: 1px solid ${({ theme }) => theme.dataTable.headerBorderColor};
  user-select: none;
  cursor: ${({ isResizing }) => (isResizing ? 'col-resize' : 'default')};
  overflow: visible;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;

  ${crFont9}
  text-transform: uppercase;
  &:last-child {
    border-right: none;
  }
`;

export const ActionsHeaderCell = styled(HeaderCell)`
  position: sticky;
  right: 0;
  background-color: ${({ theme }) => theme.dataTable.actionsColumnBackgroundColor};
  border-left: 2px solid ${({ theme }) => theme.dataTable.actionsColumnBorderColor};
  text-align: center;
  z-index: 4;
  width: 50px;
`;

export const DataCell = styled.td<{ width: number; isClickable?: boolean }>`
  width: ${({ width }) => width}px;
  min-width: ${({ width }) => width}px;
  max-width: ${({ width }) => width}px;
  height: 40px;
  padding: 0 12px;
  color: ${({ theme }) => theme.dataTable.rowTextColor};
  font-size: 14px;
  text-align: left;
  vertical-align: middle;
  border-right: 1px solid ${({ theme }) => theme.dataTable.rowBorderColor};
  border-bottom: 1px solid ${({ theme }) => theme.dataTable.rowBorderColor};
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  position: relative;
  overflow: visible;
  box-sizing: border-box;

  ${crFont11}

  &:last-child {
    border-right: none;
  }
`;

export const CellContent = styled.div<{ width?: number | string }>`
  max-width: ${({ width }) =>
    width && typeof width === 'number' ? width - 24 : width}px; /* Subtract padding */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ActionsCell = styled(DataCell)`
  position: sticky;
  right: 0;
  background-color: ${({ theme }) => theme.dataTable.actionsColumnBackgroundColor};
  border-left: 2px solid ${({ theme }) => theme.dataTable.actionsColumnBorderColor};
  text-align: center;
  padding: 0;
  width: 50px;
`;

export const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 100%;
  background-color: transparent;
  cursor: col-resize;
  z-index: 1;

  &:hover {
    background-color: ${({ theme }) => theme.dataTable.resizeHandleHoverColor};
  }

  &:active {
    background-color: ${({ theme }) => theme.dataTable.resizeHandleColor};
  }
`;

export const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: ${({ theme }) => theme.dataTable.sortIconColor};
  cursor: pointer;
  font-size: inherit;
  font-weight: inherit;
  text-transform: inherit;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.dataTable.sortIconActiveColor};
  }

  &:focus {
    outline: none;
  }
`;

export const SortIcon = styled.span<{ direction: 'asc' | 'desc' | 'none' }>`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  opacity: ${({ direction }) => (direction === 'none' ? 0.3 : 1)};
  transform: ${({ direction }) => (direction === 'desc' ? 'rotate(180deg)' : 'none')};
  transition: all 0.2s ease;
`;

export const FilterIcon = styled.button<{ isActive?: boolean }>`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${({ isActive, theme }) =>
    isActive ? theme.dataTable.sortIconActiveColor : theme.dataTable.sortIconColor};
  cursor: pointer;
  border-radius: 2px;
  z-index: 1;

  &:hover {
    background-color: ${({ theme }) => theme.dataTable.rowHoverBackgroundColor};
    color: ${({ theme }) => theme.dataTable.sortIconActiveColor};
  }

  &:focus {
    outline: none;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${({ theme }) => theme.dataTable.rowTextColor};
  font-size: 11px;
`;
